/**
 * CreateResellerDialog (TMMPDM-0015) — slug (a DNS label, lower-cased as typed) and name.
 * 409 `RESELLER_SLUG_TAKEN` → a field error on slug; 400 `VALIDATION_FAILED` → the
 * backend's per-field messages. On success the parent navigates to the new reseller.
 * TMMPDM-0017: tmm-api `POST /v1/admin/resellers`; a 502 `SAAS_AUTH_OUTCOME_UNKNOWN` is never
 * re-POSTed — the parent re-reads the list (`onOutcomeUnknown`).
 */
import { useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
} from '@mui/material';
import { useCopy } from '@/hooks/useCopy';
import { SubmitButton } from '@/components/page';
import { adminApi } from '@/api/tmm/admin';
import {
  ApiError,
  errorCopy,
  isOutcomeUnknown,
  isValidationFailed,
  validationFields,
} from '@/api/errors';
import type { ResellerDto } from '@/api/tmm/types';

/** DNS label: lowercase letters/digits, hyphens not at either end, ≤63 chars. */
const SLUG_PATTERN = /^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/;

interface CreateResellerDialogProps {
  open: boolean;
  onPendingChange?: (pending: boolean) => void;
  onClose: () => void;
  onCreated: (reseller: ResellerDto) => void;
  /** The create may or may not have happened — the parent re-reads; nothing re-POSTs. */
  onOutcomeUnknown: () => void;
}

export function CreateResellerDialog(props: CreateResellerDialogProps) {
  return (
    <Dialog open={props.open} onClose={props.onClose} fullWidth maxWidth="sm">
      {props.open ? <CreateResellerForm {...props} /> : null}
    </Dialog>
  );
}

export function CreateResellerForm({
  onClose,
  onCreated,
  onOutcomeUnknown,
  onPendingChange,
}: CreateResellerDialogProps) {
  const { t } = useCopy();
  const [slug, setSlug] = useState('');
  const [name, setName] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const reset = () => {
    setSlug('');
    setName('');
    setFieldErrors({});
    setFormError(null);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleSubmit = async () => {
    const next: Record<string, string> = {};
    if (slug.trim() === '') {
      next.slug = t('resellers.create.slugRequired', 'Slug is required.');
    } else if (!SLUG_PATTERN.test(slug.trim())) {
      next.slug = t(
        'resellers.create.slugInvalid',
        'Slug must be a valid DNS label (lowercase letters, numbers, hyphens; up to 63 characters).',
      );
    }
    if (name.trim() === '') {
      next.name = t('resellers.create.nameRequired', 'Name is required.');
    } else if (name.trim().length > 200) {
      next.name = t('resellers.create.nameTooLong', 'Name must be 200 characters or fewer.');
    }
    setFieldErrors(next);
    setFormError(null);
    if (Object.keys(next).length > 0) return;

    setSubmitting(true);
    onPendingChange?.(true);
    try {
      const created = await adminApi.createReseller({ slug: slug.trim(), name: name.trim() });
      reset();
      onCreated(created);
    } catch (err) {
      if (isOutcomeUnknown(err)) {
        reset();
        onOutcomeUnknown();
      } else if (err instanceof ApiError && err.code === 'RESELLER_SLUG_TAKEN') {
        setFieldErrors({
          slug: t('resellers.create.slugTaken', 'That slug is already used by another reseller.'),
        });
      } else if (isValidationFailed(err)) {
        setFieldErrors(
          validationFields(err, t, {
            slug: t(
              'resellers.create.slugInvalid',
              'Slug must be a valid DNS label (lowercase letters, numbers, hyphens; up to 63 characters).',
            ),
            name: t('resellers.create.nameTooLong', 'Name must be 200 characters or fewer.'),
          }),
        );
      } else {
        setFormError(
          errorCopy(
            err,
            t,
            t('resellers.create.errorGeneric', 'Could not create reseller. Please try again.'),
          ),
        );
      }
    } finally {
      setSubmitting(false);
      onPendingChange?.(false);
    }
  };

  return (
    <>
      <DialogTitle>{t('resellers.create.title', 'Create reseller')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {formError ? <Alert severity="error">{formError}</Alert> : null}
          <TextField
            label={t('resellers.create.slugLabel', 'Slug')}
            helperText={
              fieldErrors.slug ??
              t(
                'resellers.create.slugHelper',
                'Lowercase letters, numbers, hyphens — up to 63 characters.',
              )
            }
            error={Boolean(fieldErrors.slug)}
            value={slug}
            onChange={(e) => setSlug(e.target.value.toLowerCase())}
            size="small"
            fullWidth
          />
          <TextField
            label={t('resellers.create.nameLabel', 'Name')}
            helperText={fieldErrors.name}
            error={Boolean(fieldErrors.name)}
            value={name}
            onChange={(e) => setName(e.target.value)}
            size="small"
            fullWidth
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={submitting}>
          {t('resellers.cancel', 'Cancel')}
        </Button>
        <SubmitButton onClick={() => void handleSubmit()} busy={submitting}>
          {submitting
            ? t('resellers.create.submitting', 'Creating…')
            : t('resellers.create.submitButton', 'Create')}
        </SubmitButton>
      </DialogActions>
    </>
  );
}
